import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import { clsx } from 'clsx';
import * as React from 'react';
import { useRef } from 'react';
import { throttle } from 'throttle-debounce';
import { IQuickLink, TQuickLinksVariant } from '../../containers/QuickLinksContainer/types';
import { MoreButton } from './MoreButton';
import { QuickLink } from './QuickLink';
import * as style from './style.css';
import { getLinksLength, LINKS_GAP, MORE_BUTTON_LENGTH, packLinksInTwoRows } from './utils/linkLength';

interface IShortListProps {
  links: IQuickLink[];
  isExpanded: boolean;
  onMoreButtonClick(): void;
  variant?: TQuickLinksVariant;
}

export const LinksList = ({ links, isExpanded, onMoreButtonClick, variant = 'default' }: IShortListProps) => {
  const isSidebar = variant === 'sidebar';
  const ref = useRef<HTMLDivElement>(null);
  const linksMeasureRef = useRef<HTMLDivElement>(null);
  const moreMeasureRef = useRef<HTMLSpanElement>(null);

  const [linksWidth, setLinksWidth] = React.useState(0);
  const [linksCount, setLinksCount] = React.useState(links.length);
  const [isLayoutMeasured, setIsLayoutMeasured] = React.useState(false);

  /* istanbul ignore next */
  const listStyle = React.useMemo(() => {
    return isExpanded
      ? {}
      : { width: linksWidth ? `${linksWidth}px` : `calc(100% - ${MORE_BUTTON_LENGTH}px - ${LINKS_GAP}px)` };
  }, [isExpanded, linksWidth]);

  useIsomorphicLayoutEffect(() => {
    setIsLayoutMeasured(false);

    function measure() {
      const width = ref.current?.getBoundingClientRect().width;

      /* istanbul ignore next */
      if (!width) {
        return;
      }

      if (isSidebar) {
        const linkWidths = linksMeasureRef.current
          ? Array.from(linksMeasureRef.current.children).map(child => child.getBoundingClientRect().width)
          : [];
        const moreButtonWidth = moreMeasureRef.current?.getBoundingClientRect().width ?? MORE_BUTTON_LENGTH;

        if (linkWidths.length === links.length) {
          setLinksCount(packLinksInTwoRows(width, linkWidths, moreButtonWidth));
          setIsLayoutMeasured(true);
        }
      } else {
        const [length, count] = getLinksLength(width, links, { reserveMoreButtonSpace: true });
        setLinksWidth(length);
        setLinksCount(count);
        setIsLayoutMeasured(true);
      }
    }

    const throttledMeasure = throttle(200, measure);

    if (isExpanded) {
      setLinksCount(links.length);
      setIsLayoutMeasured(true);

      return;
    }

    measure();

    if (!ref.current?.getBoundingClientRect().width) {
      requestAnimationFrame(measure);
    }

    window.addEventListener('resize', throttledMeasure);

    const resizeObserver = new ResizeObserver(() => {
      throttledMeasure();
    });

    const linksBlock = ref.current;
    const linksMeasure = linksMeasureRef.current;

    if (linksBlock) {
      resizeObserver.observe(linksBlock);
    }

    if (isSidebar && linksMeasure) {
      resizeObserver.observe(linksMeasure);
    }

    return () => {
      window.removeEventListener('resize', throttledMeasure);
      resizeObserver.disconnect();
    };
  }, [isExpanded, isSidebar, links]);

  const hasHiddenLinks = linksCount < links.length;
  const showMoreButton = isExpanded || hasHiddenLinks || (isSidebar && !isLayoutMeasured && links.length > 0);
  const visibleLinks = isExpanded ? links : links.slice(0, linksCount);

  if (isSidebar) {
    return (
      <div className={style['links-block']} ref={ref}>
        <div className={clsx(style['links'], isExpanded ? style['links--expanded'] : style['links--two-rows'])}>
          {visibleLinks.map((link, index) => (
            <QuickLink link={link} key={index} />
          ))}
          {showMoreButton ? <MoreButton isExpanded={isExpanded} onClick={onMoreButtonClick} /> : null}
        </div>
        <div className={style['links-measure']} aria-hidden>
          <div ref={linksMeasureRef} className={style['links-measure-row']}>
            {links.map((link, index) => (
              <QuickLink link={link} key={index} />
            ))}
          </div>
          <span ref={moreMeasureRef}>
            <MoreButton isExpanded={false} onClick={() => undefined} />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={style['links-block']} ref={ref}>
      <div className={clsx(style['links'], isExpanded && style['links--expanded'])} style={listStyle}>
        {links.slice(0, linksCount).map((link, index) => (
          <QuickLink link={link} key={index} />
        ))}
        <MoreButton isExpanded={isExpanded} onClick={onMoreButtonClick} />
        {links.slice(linksCount).map((link, index) => (
          <QuickLink link={link} key={`rest_${index}`} />
        ))}
      </div>
    </div>
  );
};
